import { AuthScreen, Button, Select } from '@agency/ui';
import { Navigate, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { AGENCY_STAFF } from '@agency/shared';
import { API_BASE, ApiError, devLogin } from '../lib/api.js';
import { loginErrorText } from '../lib/loginErrors.js';
import { useSession } from '../lib/session.js';

export function LoginPage() {
  const session = useSession();
  const search = useSearch({ from: '/login' });
  const [devBusy, setDevBusy] = useState(false);
  const [devError, setDevError] = useState<string | null>(null);
  const [persona, setPersona] = useState<string>(AGENCY_STAFF[0]?.email ?? '');
  const returnTo = search.returnTo && search.returnTo.startsWith('/') && !search.returnTo.startsWith('//') ? search.returnTo : '/';

  if (session.status === 'authenticated') return <Navigate to={returnTo} replace />;

  const error = loginErrorText(search.error) ?? (session.error ? `The API at ${API_BASE} is not reachable.` : undefined);

  const onDevLogin = async () => {
    setDevBusy(true);
    setDevError(null);
    try {
      await devLogin(persona || undefined);
      await session.reload();
    } catch (e) {
      const err = ApiError.from(e);
      setDevError(err.code === 'not_found' ? 'Development sign-in is not enabled on the server (AGENCY_DEV_LOGIN=1).' : err.message);
    } finally {
      setDevBusy(false);
    }
  };

  return (
    <AuthScreen
      surface="Admin"
      title="Sign in"
      message="Agency administration uses your workforce identity. You return here after the identity provider confirms your invitation."
      onSignIn={() => session.login(returnTo)}
      signInLabel="Sign in"
      loading={session.status === 'loading'}
      error={error}
      footer={`${API_BASE} · ${import.meta.env.MODE}`}
    >
      {import.meta.env.DEV ? (
        <div className="border-t border-hair pt-4">
          <p className="label-mono">Development sign-in</p>
          <div className="mt-2 flex items-center gap-2">
            <Select
              value={persona}
              onValueChange={setPersona}
              aria-label="Persona"
              className="flex-1"
              options={AGENCY_STAFF.map((s) => ({ value: s.email, label: s.displayName, hint: s.status }))}
            />
            <Button variant="tertiary" onClick={() => void onDevLogin()} loading={devBusy}>Sign in as persona</Button>
          </div>
          {devError ? <p className="mt-2 text-xs text-brand-text">{devError}</p> : null}
        </div>
      ) : null}
    </AuthScreen>
  );
}
